import {stringify} from 'querystring';
import Request from 'utilities/Request';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import {CARD_PARTS} from 'app/CardConstants';
import {isRetina} from 'utilities/userAgent';

const kpiUrl = global.ENV.public ? '/cards/kpi' : '/api/content/v1/cards/kpi';

export const fetchById = (
  id,
  options = {
    parts: CARD_PARTS.join(','),
  }
) => {
  const url = global.ENV.public ? '/cards/kpi' : '/api/content/v1/cards';
  return Request.get(`${url}?urns=${id}&${stringify(options)}`);
};

export const hasCardAccess = (cardId, options) => {
  const opts = {userIds: [], groupIds: [], ...options};
  const params = {
    userIds: opts.userIds.join(','),
    groupIds: opts.groupIds.join(','),
  };
  return Request.get(
    `/api/content/v1/share/accesslist/filtered/badge/${cardId}?${stringify(
      params
    )}`
  );
};

export const grantAccessToCard = (cardId, userIds, groupIds) => {
  const users = userIds.map(id => ({id, type: 'user'}));
  const groups = groupIds.map(id => ({id, type: 'group'}));
  return Request.post('/api/content/v1/share', {
    resources: [{type: 'badge', id: cardId}],
    recipients: [...users, ...groups],
  });
};

export const fetchStatistics = (cardIds, options) => {
  const params = stringify({
    urns: cardIds.join(','),
    ...options,
  });
  return Request.get(`/api/content/v1/cards/stats?${params}`);
};

export const renderCardById = (cardId, options) => {
  options = options || {};
  const body = {};
  const query = pick(options, [
    'imageAsDocument',
    'includeDateInfo',
    'includeDrillFilter',
    'mobileClientType',
    'size',
    'type',
  ]);
  query.retina = isRetina;
  query.mobileClientType = 'web';

  if (options.filters) {
    query.filter = JSON.stringify(options.filters);
  }

  if (options.dateTimeElement) {
    body.dateTimeElement = options.dateTimeElement;
    body.filters = options.filters;
  }

  if (!isUndefined(options.dateRangeFilter)) {
    body.filters = options.filters;
    body.dateRangeFilter = options.dateRangeFilter;
  }

  if (options.filterGroupIds && options.filterGroupIds.length > 0) {
    body.filters = options.filters;
    body.dataControlContext = {
      filterGroupIds: options.filterGroupIds,
    };
  }

  const params = stringify(omit(query));
  const url = `/cards/${cardId}/render?${params}`;

  if (isEmpty(body)) {
    return Request.get(url);
  } else {
    return Request.post(url, body);
  }
};

export const fetchSuperTable = (cardId, params) =>
  Request.get(`/api/content/v1/sumo/${cardId}?${stringify(params)}`);

export const saveAs = (cardId, {dateRangeFilter, ...rest}) =>
  Request.post(`/api/content/v1/cards/${cardId}/saveas`, {
    ...rest,
    dateRangeFilter,
    overwriteDateRange: !!dateRangeFilter,
  });

// only pass through the filter properties that the backend expects
// Should be mostly in sync with the function with the
// same name in desktop web in DomoWeb/public/javascripts/kpis/ChartUtils.ts
const phoenixFilterToFilter = (filter, isAddFilterTypeEnabled) => {
  return {
    cardURN: filter.cardURN,
    label: filter.label,
    column: filter.column,
    operand: filter.operand,
    values: filter.values,
    dataSourceId: filter.dataSourceId,
    dataType: filter.dataType,
    dateJoinColumn: filter.dateJoinColumn,
    fiscal: filter.fiscal,
    aggregated: filter.aggregated,
    filterType:
      isAddFilterTypeEnabled ||
      filter.dateGrainFilter ||
      filter.filterType === 'CALENDAR'
        ? filter.filterType
        : undefined,
  };
};

export const notebookGetDynamicTextItems = (card)=> {
  return card.metadata.dynamicTextItems && JSON.parse(card.metadata.dynamicTextItems);
};

export const notebookHasDynamicText = (card)=> {
  const dynamicTextItems = notebookGetDynamicTextItems(card);
  return dynamicTextItems && Object.keys(dynamicTextItems).length > 0;
};

export const notebookHasDynamicTextOfType = (card, type) => {
  const dynamicTextItems = notebookGetDynamicTextItems(card);
  if (!dynamicTextItems) return false;
  return Object.values(dynamicTextItems).some((item)=> item.type === type);
};

// the notebook backend just uses the "expression" field, not the "parsedExpression"
export const populateFunctionOverrideExpressions = (functionOverrides) => {
  return Object.keys(functionOverrides).reduce((acc, key)=> {
    const override = functionOverrides[key];
    return {
      ...acc,
      [key]: {
        ...override,
        ...(!override.expression && override.parsedExpression && {
          expression: override.parsedExpression.value,
        }),
      },
    };
  }, {});
};

export const formatQueryOverrides = ({
                                filters,
                                isAddFilterTypeEnabled,
                                segments,
                                dataControlContext,
                                dateRangeFilter,
                                dateTimeElement,
                                limit,
                                offset,
                                overrideDateRange,
                                functionOverrides,
                                overrideSlicers
                              }) => {
  return {
    filters: (filters || []).map(f =>
      phoenixFilterToFilter(f, isAddFilterTypeEnabled)
    ),
    segments: segments && segments.length > 0 ? segments : undefined,
    dataControlContext,
    dateRangeFilter,
    dateTimeElement,
    limit,
    offset,
    overrideDateRange,
    functionOverrides,
    overrideSlicers,
  };
}

export const formatBody = ({
  accept,
  chartState,
  chartType,
  console,
  dataControlContext,
  dateRangeFilter,
  dateTimeElement,
  overrideDateRange,
  functionOverrides,
  filters,
  segments,
  formatData,
  height,
  imageMap,
  item,
  limit,
  locale,
  mobile,
  offset,
  overrideSlicers,
  pageLayout,
  scale,
  size,
  type,
  width,
  isAddFilterTypeEnabled,
  cardLoadContext,
}) => {
  if (isUndefined(overrideDateRange)) {
    // Anytime dateRangeFilter is defined, even as null, we set overrideDateRange to true.
    // The reason for this logic is because a dateRangeFilter of AllTime is
    // specified by not sending a dateRangeFilter property or setting the property
    // to null and so the way we override
    // a non ALLTime dateRangeFilter to use AllTime is to set dateRangeFilter
    // to null and set overrideDateRange to true.
    overrideDateRange = isUndefined(dateRangeFilter) ? undefined : true;
  }
  return {
    accept,
    chartState,
    chartType,
    console,
    formatData,
    height,
    imageMap,
    item,
    locale,
    mobile,
    pageLayout,
    scale,
    size,
    type,
    width,
    cardLoadContext,
    queryOverrides: formatQueryOverrides({
      filters,
      isAddFilterTypeEnabled,
      segments,
      dataControlContext,
      dateRangeFilter,
      dateTimeElement,
      limit,
      offset,
      overrideDateRange,
      functionOverrides,
      overrideSlicers
    }),
  };
};

export const fetchChart = (id, {parts, ...options}) => {
  const params = stringify({parts});
  const body = formatBody(options);

  const url = global.ENV.embed ? '/api/content/v1/cards/kpi/embed' : kpiUrl;
  const promise = Request.put(`${url}/${id}/render?${params}`, body);

  const chained = promise.then(response => ({
    ...response,
    bridge: JSON.stringify(response.bridge),
    chart: JSON.stringify(response.chart),
  }));

  chained.cancel = promise.cancel;

  return chained;
};

export const fetchTable = (id, options) => {
  const body = formatBody(options);
  return Request.put(`${kpiUrl}/${id}/table`, body);
};

export const fetchEndOfDrill = (id, options) => {
  const body = formatBody(options);
  return Request.put(`/api/content/v1/cards/kpi/${id}/endofdrill`, body);
};

export const fetchCardExport = (id, options) => {
  const body = formatBody(options);
  const url = global.ENV.public
    ? `/cards/kpi/${id}/export`
    : `/api/content/v1/cards/${id}/export`;

  return Request.post(url, stringify({request: JSON.stringify(body)}), {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    responseType: 'blob',
  });
};

export const fetchDomoAppPreview = (id, body) => {
  return Request.put(`/api/davinci/v1/image/png/${id}`, body, {
    responseType: 'blob',
    // most cards return in less than 30 seconds
    timeout: 30000,
  });
};

export const fetchNotebookCard = (id, options) => {
  const body = formatBody(options);
  return Request.post(`/api/content/v1/cards/notebook/${id}`, body);
};
